import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Backdrop, CircularProgress } from '@mui/material';
import PLApp from './PLApp';
import { ServiceApi } from '../../services/index.service';
import './pl-tool.css';

const PL_ALLOWED_ROLES = ["Super Admin", "Admin", "Franchisee", "Director(FC Lead)", "Franchise Consultant"];
// Okta app label (lowercase) that grants P&L Submission access - keep in sync with AdminDashboard.js
const PL_OKTA_LABELS = ["p&l submission"];

/**
 * Shell component that gates the P&L tool behind Okta auth + store selection,
 * a role allowlist, and Okta "P&L Submission" app assignment (checked directly
 * here so navigating straight to /profit-loss can't bypass the dashboard-tile gate),
 * extracts Redux state, and renders the scoped PLApp inside .pl-container.
 */
const ProfitLossTool = () => {
  const navigate = useNavigate();
  const serviceapi = new ServiceApi();
  const { accessToken } = useSelector((state) => state?.token) || {};
  const { profile, storage } = useSelector((state) => state) || {};
  const store = storage?.store;
  const [accessChecked, setAccessChecked] = useState(false);
  const [accessAllowed, setAccessAllowed] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      if (!profile?.role || !profile?.sub) return;

      if (!PL_ALLOWED_ROLES.includes(profile.role)) {
        setAccessChecked(true);
        setAccessAllowed(false);
        navigate('/403');
        return;
      }

      try {
        const res = await serviceapi.getApps(profile.sub);
        const apps = Array.isArray(res?.data) ? res.data : (res?.data?.body || []);
        const labels = apps.map((a) => a.label?.toLowerCase());
        const hasAccess = PL_OKTA_LABELS.some((label) => labels.includes(label));
        setAccessAllowed(hasAccess);
        setAccessChecked(true);
        if (!hasAccess) {
          navigate('/403');
        }
      } catch (err) {
        console.error('ProfitLossTool: failed to verify Okta app assignment:', err);
        setAccessAllowed(false);
        setAccessChecked(true);
        navigate('/403');
      }
    };
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role, profile?.sub]);

  if (!accessToken || !store || !profile?.role || !accessChecked || !accessAllowed) {
    return (
      <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <div className="pl-container">
      <PLApp
        intStoreID={store?.intStoreID}
        storeNumber={store?.intRitasStoreID}
        role={profile?.role}
        userName={profile?.name || profile?.preferred_username || 'Unknown User'}
      />
    </div>
  );
};

export default ProfitLossTool;
